<template>
  <div class="card">
    <div class="card-header d-flex align-items-center justify-content-between">
      <h3 class="card-title mb-0">Fișă Cont</h3>
      <div class="d-flex gap-2">
        <button type="button" class="btn btn-sm btn-light-success fw-bold" @click="loadSpecial('5311')">
          <i class="fa-solid fa-cash-register me-1"></i> Registru de casă
        </button>
        <button type="button" class="btn btn-sm btn-light-info fw-bold" @click="loadSpecial('5121')">
          <i class="fa-solid fa-building-columns me-1"></i> Extras de cont
        </button>
        <button type="button" class="btn btn-sm btn-primary" @click="openNotaContabilaModal">
          <i class="fa fa-plus me-1"></i> Notă Contabilă
        </button>
        <button type="button" class="btn btn-sm btn-light-primary" @click="loadData">
          <i class="fa fa-rotate-right me-1"></i> Reîncarcă
        </button>
      </div>
    </div>
    <div class="card-body">
      <!-- Filtre Fișa Cont -->
      <div class="mb-5">
        <form @submit.prevent="loadData">
          <div class="row g-3 align-items-end">
            <div class="col-md-3">
              <label class="form-label">Contul</label>
              <multiselect
                v-model="cont_obj"
                :options="conturi"
                track-by="id"
                label="searchLabel"
                :custom-label="contLabel"
                placeholder="Caută un cont (cod sau denumire)..."
                class="w-100 small-text-multiselect"
                :searchable="true"
                select-label=""
                selected-label=""
                deselect-label=""
              >
              </multiselect>
            </div>
            <div class="col-md-7 mt-2 mt-md-0">
              <div class="d-flex flex-column gap-2">
                <!-- Rândul 1: De la + Luna -->
                <div class="d-flex align-items-center gap-4 flex-wrap">
                  <div class="d-flex align-items-center gap-2">
                    <label class="mb-0 fw-bold" style="min-width: 60px;">De la:</label>
                    <input type="date" class="form-control form-control-sm" v-model="filters.date_start" style="width: 140px;" />
                  </div>
                  <div class="d-flex align-items-center gap-2">
                    <label class="mb-0 fw-bold">Luna:</label>
                    <select class="form-select form-select-sm" v-model.number="selected_month" style="width: 110px;">
                      <option v-for="(name, m) in monthNames" :key="'m'+m" :value="m+1">{{ name }}</option>
                    </select>
                  </div>
                </div>
                <!-- Rândul 2: Până la + An/Săgeți -->
                <div class="d-flex align-items-center gap-4 flex-wrap">
                  <div class="d-flex align-items-center gap-2">
                    <label class="mb-0 fw-bold" style="min-width: 60px;">Până la:</label>
                    <input type="date" class="form-control form-control-sm" v-model="filters.date_end" style="width: 140px;" />
                  </div>
                  <div class="d-flex align-items-center gap-2">
                    <button type="button" class="btn btn-xs btn-icon btn-light-primary" @click="prevMonth" title="Luna anterioară">
                      <i class="fa-solid fa-chevron-up"></i>
                    </button>
                    <button type="button" class="btn btn-xs btn-icon btn-light-primary" @click="nextMonth" title="Luna următoare">
                      <i class="fa-solid fa-chevron-down"></i>
                    </button>
                    <input type="number" class="form-control form-control-sm" v-model.number="selected_year" style="width: 70px;" />
                  </div>
                </div>
              </div>
            </div>
            <div class="col-md-2">
              <button type="submit" class="btn btn-primary w-100" :disabled="!filters.id_cont || loading">
                <i class="fa fa-search me-1"></i>
                Caută
              </button>
            </div>
          </div>
        </form>
      </div>

      <!-- Tabel Rezultate -->
      <div v-if="loading" class="text-center py-4">
        Se calculează rulajele...
      </div>
      <div v-else-if="!fisaContData" class="text-center text-muted py-4">
        Selectează un cont pentru a vizualiza fișa.
      </div>
      <div v-else>
        <!-- Header Fișa -->
        <div class="alert alert-light-primary border-primary d-flex justify-content-between align-items-center mb-4">
          <div>
            <h5 class="mb-1 text-primary fw-bold">
              Cont: {{ fisaContData.cont.cod }} - {{ fisaContData.cont.descriere }}
            </h5>
            <div class="text-muted small">
              Perioada: {{ fisaContData.perioada.start }} — {{ fisaContData.perioada.end }}
              | Tip Cont (Calcul): <strong>{{ tipContDisplay(fisaContData.cont.tip) }}</strong>
            </div>
          </div>
        </div>

        <!-- Tabel Date -->
        <div class="table-responsive">
          <table class="table table-bordered align-middle table-hover">
            <thead class="bg-light fw-bold text-uppercase fs-7">
              <tr>
                <th style="width: 100px;">Data</th>
                <th style="width: 120px;">Document</th>
                <th style="width: 140px;">Tip</th>
                <th>Explicație</th>
                <th style="width: 100px;">Ct. Corpesp.</th>
                <th style="width: 120px;" class="text-end">Debit</th>
                <th style="width: 120px;" class="text-end">Credit</th>
                <th style="width: 180px;" class="text-end">Sold Curent</th>
                <th style="width: 80px;" class="text-center">Acțiuni</th>
              </tr>
            </thead>
            <tbody>
              <!-- Rulaj Precedent (Sold Inițial) -->
              <tr class="table-secondary fw-semibold">
                <td colspan="5" class="text-end text-muted">SOLD PRECEDENT PÂNĂ LA {{ formatDate(fisaContData.perioada.start) }}</td>
                <td class="text-end text-muted">{{ fisaContData.sold_initial.tip === 'DEBIT' ? formatMoney(fisaContData.sold_initial.suma) : '' }}</td>
                <td class="text-end text-muted">{{ fisaContData.sold_initial.tip === 'CREDIT' ? formatMoney(fisaContData.sold_initial.suma) : '' }}</td>
                <td class="text-end fs-6" :class="soldColor(fisaContData.sold_initial.tip)">
                    {{ formatMoney(fisaContData.sold_initial.suma) }}
                    <span class="badge badge-light ms-1">{{ fisaContData.sold_initial.tip.charAt(0) }}</span>
                </td>
                <td></td>
              </tr>

              <!-- Tranzacții din interval -->
              <tr v-for="(t, idx) in fisaContData.tranzactii" :key="idx">
                <td>{{ formatDate(t.doc_data) }}</td>
                <td><strong>{{ t.document || '-' }}</strong></td>
                <td><small class="text-muted">{{ t.tip_document }}</small></td>
                <td>{{ t.explicatie }}</td>
                <td><span class="badge badge-light-primary">{{ t.cont_corespondent }}</span></td>
                <td class="text-end">{{ t.debit ? formatMoney(t.debit) : '' }}</td>
                <td class="text-end">{{ t.credit ? formatMoney(t.credit) : '' }}</td>
                <td class="text-end fw-semibold">
                   {{ formatMoney(t.sold) }}
                   <span class="badge ms-1" :class="t.sold_tip === 'DEBIT' ? 'badge-light-primary' : 'badge-light-warning'">{{ t.sold_tip.charAt(0) }}</span>
                </td>
                <td class="text-center">
                  <div class="d-flex justify-content-center gap-1">
                    <button type="button" class="btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1" 
                            @click="editTransaction(t)" title="Editează">
                      <i class="fa-solid fa-pencil fs-6"></i>
                    </button>
                    <button type="button" class="btn btn-icon btn-bg-light btn-active-color-danger btn-sm" 
                            @click="confirmDelete(t)" title="Șterge definitiv">
                      <i class="fa-solid fa-trash fs-6"></i>
                    </button>
                  </div>
                </td>
              </tr>
              
              <!-- Niciun rulaj in interval -->
              <tr v-if="fisaContData.tranzactii.length === 0">
                 <td colspan="9" class="text-center text-muted py-3">Nu există tranzacții în perioada selectată.</td>
              </tr>

              <!-- Total Rulaje Perioada -->
              <tr class="table-light fw-bold text-muted">
                <td colspan="5" class="text-end">TOTAL RULAJE INTERVAL</td>
                <td class="text-end text-primary">{{ formatMoney(fisaContData.rulaje_perioada.debit) }}</td>
                <td class="text-end text-warning">{{ formatMoney(fisaContData.rulaje_perioada.credit) }}</td>
                <td></td>
              </tr>

              <!-- Sold Final -->
              <tr class="table-primary fw-bold fs-5">
                <td colspan="5" class="text-end text-primary">SOLD FINAL LA {{ formatDate(fisaContData.perioada.end) }}</td>
                <td class="text-end">{{ fisaContData.sold_final.tip === 'DEBIT' ? formatMoney(fisaContData.sold_final.suma) : '' }}</td>
                <td class="text-end">{{ fisaContData.sold_final.tip === 'CREDIT' ? formatMoney(fisaContData.sold_final.suma) : '' }}</td>
                <td class="text-end">
                    {{ formatMoney(fisaContData.sold_final.suma) }}
                    <span class="badge ms-2" :class="fisaContData.sold_final.tip === 'DEBIT' ? 'bg-primary' : 'bg-warning text-dark'">{{ fisaContData.sold_final.tip }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>


    <NotaContabilaModal ref="notaModal" :firma-id="firmaId" @saved="loadData" />

    <IncasareModal
      v-if="showIncasareModal"
      :firma-id="firmaId"
      :client-id="editDocClientId"
      :incasare-id="editDocId"
      mode="edit"
      :allow-accounting-edit="true"
      :allow-series-edit="true"
      @close="showIncasareModal = false"
      @saved="loadData"
    />

    <!-- Modal Plată (Chitanță Furnizor/Plată) -->
    <PlataModal
      v-if="showPlataModal"
      :firma-id="firmaId"
      :furnizor-id="editDocFurnizorId"
      :plata-id="editDocId"
      mode="edit"
      :allow-series-edit="true"
      @close="showPlataModal = false"
      @saved="loadData"
    />
  </div>
</template>

<script>
import NotaContabilaModal from './NotaContabilaModal.vue';
import IncasareModal from '../Facturare/Iesiri/Facturi/IncasareModal.vue';
import PlataModal from '../Administrare/work_components/PlataModal.vue';
import Multiselect from 'vue-multiselect';
import Swal from 'sweetalert2';

export default {
  name: 'FisaCont',
  components: { NotaContabilaModal, IncasareModal, PlataModal, Multiselect },
  props: {
    firmaId: {
      type: Number,
      required: true
    }
  },
  data() {
    const today = new Date();
    return {
      filters: {
        id_cont: '',
        date_start: '',
        date_end: '',
      },
      selected_month: today.getMonth() + 1,
      selected_year: today.getFullYear(),
      monthNames: [
        'Ianuarie', 'Februarie', 'Martie', 'Aprilie', 'Mai', 'Iunie',
        'Iulie', 'August', 'Septembrie', 'Octombrie', 'Noiembrie', 'Decembrie'
      ],
      conturi: [],
      cont_obj: null,
      fisaContData: null,
      loading: false,

      // Editare documente din fisa cont
      showIncasareModal: false,
      showPlataModal: false,
      editDocId: null,
      editDocClientId: null,
      editDocFurnizorId: null,
    }
  },
  mounted() {
    this.fetchConturi();
    this.autoSetDates();
  },
  methods: {
    formatDate(d) {
      if (!d) return '';
      try {
        return new Date(d).toLocaleDateString('ro-RO');
      } catch (e) {
        return '';
      }
    },
    formatMoney(v) {
      const n = Number(v || 0);
      if (n === 0) return '-';
      return n.toLocaleString('ro-RO', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    tipContDisplay(tip) {
        if (tip === 'A') return 'Activ (Sold Debitor)';
        if (tip === 'P') return 'Pasiv (Sold Creditor)';
        if (tip === 'B') return 'Bifuncțional (Activ)';
        return 'Necunoscut';
    },
    contLabel(option) {
      if (!option) return '';
      const cod = option.cod_complet ?? option.cod ?? option.id ?? '';
      const den = option.descriere ?? option.denumire ?? option.text ?? option.label ?? '';
      return den ? `${cod} | ${den}` : String(cod);
    },
    soldColor(tip) {
        if (tip === 'DEBIT') return 'text-primary';
        if (tip === 'CREDIT') return 'text-warning text-darken';
        return '';
    },
    async fetchConturi() {
      try {
         const res = await axios.get('/api/contabilitate/plan_conturi', {
             params: { id_firma: this.firmaId }
         });
         this.conturi = res.data.map(c => ({
             ...c,
             searchLabel: `${c.cod_complet} ${c.descriere}`
         }));
      } catch (e) {
          console.error("Nu s-au putut obține conturile", e);
      }
    },
    autoSetDates() {
      const year = this.selected_year;
      const month = this.selected_month;
      
      // First day of month
      this.filters.date_start = `${year}-${String(month).padStart(2, '0')}-01`;
      
      // Last day of month
      const lastDay = new Date(year, month, 0).getDate();
      this.filters.date_end = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    },
    prevMonth() {
      if (this.selected_month === 1) {
        this.selected_month = 12;
        this.selected_year--;
      } else {
        this.selected_month--;
      }
    },
    nextMonth() {
      if (this.selected_month === 12) {
        this.selected_month = 1;
        this.selected_year++;
      } else {
        this.selected_month++;
      }
    },
    async loadSpecial(cod) {
        this.filters.id_cont = 'cod:' + cod;
        this.cont_obj = null;
        await this.loadData();
    },
    async loadData() {
        if (!this.filters.id_cont) {
            Swal.fire("Atenție", "Alege un cont mai întâi.", "warning");
            return;
        }

        this.loading = true;
        this.fisaContData = null;

        try {
            const res = await axios.post(`/api/contabilitate/fisa_cont/${this.filters.id_cont}`, {
                date_start: this.filters.date_start,
                date_end: this.filters.date_end,
            });
            this.fisaContData = res.data;
        } catch (e) {
            console.error('Eroare load fisa cont', e);
            const msg = e?.response?.data?.error || 'Eroare la calcularea fișei de cont.';
            Swal.fire("Eroare", msg, "error");
        }
        this.loading = false;
    },
    openNotaContabilaModal() {
        if(this.$refs.notaModal) {
            this.$refs.notaModal.showModal();
        }
    },
    async confirmDelete(t) {
        if (!t.rj_id) {
            Swal.fire("Atenție", "Această tranzacție nu poate fi ștearsă din Fișa de Cont.", "warning");
            return;
        }

        const result = await Swal.fire({
            title: 'Sunteți sigur?',
            text: "Această acțiune este DEFINITIVĂ și IREVOCABILĂ și va șterge documentul de peste tot din sistem!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Da, șterge definitiv!',
            cancelButtonText: 'Anulează'
        });
        
        if (result.isConfirmed) {
            try {
                const res = await axios.delete(`/api/contabilitate/document/${t.rj_id}`);
                Swal.fire("Șters!", res.data.message, "success");
                this.loadData();
            } catch (e) {
                console.error("Eroare la ștergere", e);
                const errorMsg = e?.response?.data?.error || "Eroare la ștergerea documentului.";
                Swal.fire("Eroare", errorMsg, "error");
            }
        }
    },
    async editTransaction(t) {
        if (!t.rj_id) {
            Swal.fire("Atenție", "Această tranzacție nu poate fi editată.", "warning");
            return;
        }

        const tip = (t.tip_document || '').toLowerCase();
        const id_doc = t.id_document;

        if (tip.includes('nota contabila')) {
            if (this.$refs.notaModal) {
                this.$refs.notaModal.editNota(t.rj_id);
            }
        } else {
            // Încercăm să identificăm dacă este încasare sau plată
            // Dacă tipul conține explicit "incasare" sau "plata", mergem direct acolo.
            // Dacă este ceva generic gen "Chitanta", încercăm ambele.

            let identified = false;

            if (tip.includes('incasare')) {
                try {
                    const res = await axios.get(`/api/facturare/incasari/item/${id_doc}`);
                    this.editDocId = id_doc;
                    this.editDocClientId = res.data.id_client;
                    this.showIncasareModal = true;
                    identified = true;
                } catch (e) {}
            } else if (tip.includes('plata')) {
                try {
                    const res = await axios.get(`/api/facturare/plati/item/${id_doc}`);
                    this.editDocId = id_doc;
                    this.editDocFurnizorId = res.data.id_furnizor;
                    this.showPlataModal = true;
                    identified = true;
                } catch (e) {}
            }

            if (!identified) {
                // Dacă nu am reușit sau este generic (ex: "Chitanta"), încercăm pe rând.
                // Încercăm Încasări
                try {
                    const resInc = await axios.get(`/api/facturare/incasari/item/${id_doc}`);
                    // Verificăm dacă reg_jurnal_id se potrivește (siguranță extra)
                    if (resInc.data.incasare && resInc.data.incasare.reg_jurnal_id === t.rj_id) {
                        this.editDocId = id_doc;
                        this.editDocClientId = resInc.data.id_client;
                        this.showIncasareModal = true;
                        identified = true;
                    }
                } catch (e) {}

                if (!identified) {
                    // Încercăm Plăți
                    try {
                        const resPl = await axios.get(`/api/facturare/plati/item/${id_doc}`);
                        if (resPl.data.plata && resPl.data.plata.reg_jurnal_id === t.rj_id) {
                            this.editDocId = id_doc;
                            this.editDocFurnizorId = resPl.data.id_furnizor;
                            this.showPlataModal = true;
                            identified = true;
                        }
                    } catch (e) {}
                }
            }

            if (!identified) {
                Swal.fire("Informație", `Editarea pentru documentul de tip "${t.tip_document}" nu este încă implementată sau documentul sursă nu a fost găsit.`, "info");
            }
        }
    }
  },
  watch: {
      firmaId(newVal) {
          this.fetchConturi();
          this.fisaContData = null;
          this.filters.id_cont = '';
          this.cont_obj = null;
      },
      selected_month() {
          this.autoSetDates();
      },
      selected_year() {
          this.autoSetDates();
      },
      cont_obj(newVal) {
          if (newVal) {
              this.filters.id_cont = newVal.id;
          } else if (!String(this.filters.id_cont || '').startsWith('cod:')) {
              this.filters.id_cont = '';
          }
      }
  }
}
</script>

<style scoped>
.text-darken {
    color: #e6a800 !important; /* Slightly darker warning for readability */
}

/* Vue-Multiselect tuning */
.multiselect {
  min-width: 0;
}

:deep(.multiselect__input),
:deep(.multiselect__single),
:deep(.multiselect__tag),
:deep(.multiselect__placeholder),
:deep(.multiselect__content) {
  color: #181C32 !important;
}

:deep(.multiselect__tags) {
  background-color: #fff;
  border: 1px solid #ced4da;
  min-height: 31px;
  padding: 4px 30px 4px 8px;
}

:deep(.multiselect__select) {
  height: 31px;
}

:deep(.multiselect__placeholder) {
  margin-bottom: 0;
  padding-top: 0;
}

/* Stil pentru cont scris mai mic, similar cu UM din facturare */
.small-text-multiselect :deep(.multiselect__single),
.small-text-multiselect :deep(.multiselect__option) {
  font-size: 0.85rem !important;
  white-space: normal !important;
  line-height: 1.25 !important;
}
</style>
